import type { ApiFetchResult } from "@/lib/apiClient";

/**
 * Every error code the MODULE 1 API CONTRACT names, plus the transport-level
 * ones apiFetch synthesizes itself. Screens branch on these, never on HTTP
 * status or message text.
 */
export type ContractErrorCode =
  | "EMAIL_ALREADY_REGISTERED"
  | "INVITE_ALREADY_PENDING"
  | "INVITE_NOT_RESENDABLE"
  | "INVITE_NOT_CANCELLABLE"
  | "INVITE_INVALID"
  | "INVITE_EXPIRED"
  | "INVALID_CREDENTIALS"
  | "VALIDATION_ERROR"
  | "REVIEW_ITEM_NOT_FOUND"
  | "ITEM_NOT_AWAITING_INPUT"
  | "ITEM_ALREADY_CLAIMED"
  | "ITEM_NOT_RELEASABLE"
  | "ITEM_NOT_DECIDABLE"
  | "ITEM_NOT_ASSIGNABLE"
  | "PHYSICIAN_NOT_ELIGIBLE"
  | "PATIENT_NOT_FOUND"
  | "PHYSICIAN_NOT_FOUND"
  | "NETWORK_ERROR"
  | "TIMEOUT"
  | "UNKNOWN_ERROR";

const CONTRACT_CODES = new Set<string>([
  "EMAIL_ALREADY_REGISTERED",
  "INVITE_ALREADY_PENDING",
  "INVITE_NOT_RESENDABLE",
  "INVITE_NOT_CANCELLABLE",
  "INVITE_INVALID",
  "INVITE_EXPIRED",
  "INVALID_CREDENTIALS",
  "VALIDATION_ERROR",
  "REVIEW_ITEM_NOT_FOUND",
  "ITEM_NOT_AWAITING_INPUT",
  "ITEM_ALREADY_CLAIMED",
  "ITEM_NOT_RELEASABLE",
  "ITEM_NOT_DECIDABLE",
  "ITEM_NOT_ASSIGNABLE",
  "PHYSICIAN_NOT_ELIGIBLE",
  "PATIENT_NOT_FOUND",
  "PHYSICIAN_NOT_FOUND",
  "NETWORK_ERROR",
  "TIMEOUT",
]);

export interface ContractError {
  /** A ContractErrorCode, or a code this module passes through untouched (the
   *  OTP endpoints reuse the patient flow's OTP_EXPIRED / TOO_MANY_ATTEMPTS). */
  code: ContractErrorCode | (string & {});
  message: string;
  status: number;
  retryAfterSeconds?: number;
}

export interface ContractResult<T> {
  data: T | null;
  error: ContractError | null;
}

/** Pulls a code string out of the error body, whichever of the common shapes the backend settles on. */
function readRawCode(raw: unknown): string | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const body = raw as Record<string, unknown>;
  const candidates = [
    body.code,
    (body.error as Record<string, unknown> | undefined)?.code,
    (body.detail as Record<string, unknown> | undefined)?.code,
    typeof body.detail === "string" ? body.detail : undefined,
  ];
  return candidates.find((c): c is string => typeof c === "string" && CONTRACT_CODES.has(c))
    ?? candidates.find((c): c is string => typeof c === "string");
}

/**
 * THE one place Module 1 turns a failed response into a contract error code.
 * If the backend's final error shape differs from the project's existing
 * { code, message } convention, adjust readRawCode above and nothing else.
 *
 * Status fallbacks only apply when no recognisable code came back, and only
 * where the contract pins a status to a single code (410, 401). Validation
 * errors are 400 VALIDATION_ERROR and arrive with their code — 400 is shared
 * with other codes (e.g. the OTP errors), so it gets no status fallback.
 */
/** The backend's shared validation handler prefixes messages with "Value error, ". */
const VALUE_ERROR_PREFIX = /^value error,\s*/i;

/** Strips that prefix and capitalises what remains; other messages pass through untouched. */
export function cleanErrorMessage(message: string): string {
  if (!VALUE_ERROR_PREFIX.test(message)) return message;
  const rest = message.replace(VALUE_ERROR_PREFIX, "");
  return rest.charAt(0).toUpperCase() + rest.slice(1);
}

export function parseContractError(result: Pick<ApiFetchResult, "status" | "error">): ContractError {
  const { status, error } = result;
  const message = cleanErrorMessage(error?.message ?? "Something went wrong.");

  let code: string | undefined = error?.code && error.code !== "UNKNOWN_ERROR" ? error.code : undefined;
  if (!code || !CONTRACT_CODES.has(code)) code = readRawCode(error?.raw) ?? code;

  if (!code || code === "UNKNOWN_ERROR") {
    if (status === 410) code = "INVITE_EXPIRED";
    else if (status === 401) code = "INVALID_CREDENTIALS";
    else code = "UNKNOWN_ERROR";
  }

  return { code, message, status, retryAfterSeconds: error?.retryAfterSeconds };
}

export function toContractResult<T>(result: ApiFetchResult<T>): ContractResult<T> {
  if (result.ok) return { data: result.data, error: null };
  return { data: null, error: parseContractError(result) };
}
